import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import EmployeeDetailModal from './EmployeeDetailModal';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const getAvatarUrl = (employee) => {
  if (employee.avatar?.filePath) {
    return `${apiUrl}/${employee.avatar.filePath.replace(/\\/g, '/')}`;
  }

  return null;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('tr-TR');
};

const getRoleText = (role) => {
  switch (role) {
    case 'admin':
      return 'Yönetici';
    case 'manager':
      return 'Müdür';
    default:
      return 'Çalışan';
  }
};

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'admin':
      return 'modern-employee-badge-admin';
    case 'manager':
      return 'modern-employee-badge-manager';
    default:
      return 'modern-employee-badge-employee';
  }
};

const TumCalisanlarPage = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const { toasts, addToast, removeToast } = useToast();

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getUsers({
        page: currentPage,
        limit: 12,
        search: searchTerm,
        department: departmentFilter,
        role: roleFilter,
        status: statusFilter
      });

      if (response.success) {
        setEmployees(response.users || []);
        setTotalPages(response.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Employees fetch error:', error);
      addToast('Çalışan listesi alınamadı.', 'error', 4000);
    } finally {
      setLoading(false);
    }
  }, [addToast, currentPage, searchTerm, departmentFilter, roleFilter, statusFilter]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const fetchDepartments = async () => {
    try {
      const response = await api.getDepartments();
      if (response.success) {
        setDepartments(response.departments || []);
      }
    } catch (error) {
      console.error('Departments fetch error:', error);
    }
  };

  const summary = useMemo(
    () => ({
      active: employees.filter((employee) => employee.isActive).length,
      visible: employees.filter((employee) => employee.visibility?.directory !== false).length
    }),
    [employees]
  );

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>Tüm Çalışanlar</h1>
              <p>İK kaydı oluşturulmuş çalışanlar, roller ve rehber görünürlüğü</p>
            </div>
            <div className="directory-admin-summary">
              <span>{employees.length} listelenen kayıt</span>
              <span>{summary.active} aktif çalışan</span>
              <span>{summary.visible} rehber görünürlüğü açık</span>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-filter-alt"></i>
              <h4>Filtreler</h4>
            </div>
          </div>
          <div className="modern-hr-filters">
            <div className="row">
              <div className="col-md-4">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Arama</label>
                  <div className="modern-hr-search-input">
                    <i className="bx bx-search"></i>
                    <input
                      className="modern-hr-form-input"
                      value={searchTerm}
                      onChange={(event) => {
                        setSearchTerm(event.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Ad, e-posta, unvan veya departman"
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Departman</label>
                  <select
                    className="modern-hr-form-select"
                    value={departmentFilter}
                    onChange={(event) => {
                      setDepartmentFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Tüm departmanlar</option>
                    {departments.map((department) => (
                      <option key={department} value={department}>
                        {department}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Rol</label>
                  <select
                    className="modern-hr-form-select"
                    value={roleFilter}
                    onChange={(event) => {
                      setRoleFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Tüm roller</option>
                    <option value="employee">Çalışan</option>
                    <option value="manager">Müdür</option>
                    <option value="admin">Yönetici</option>
                  </select>
                </div>
              </div>
              <div className="col-md-2">
                <div className="modern-hr-form-group">
                  <label className="modern-hr-form-label">Durum</label>
                  <select
                    className="modern-hr-form-select"
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Tümü</option>
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-hr-widget">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-group"></i>
              <h4>Çalışan listesi</h4>
            </div>
          </div>

          {loading ? (
            <div className="modern-hr-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              <p>Çalışanlar yükleniyor...</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="modern-hr-empty">
              <i className="bx bx-user-x"></i>
              <h5>Çalışan bulunamadı</h5>
              <p>Seçtiğiniz filtrelerle eşleşen kayıt yok.</p>
            </div>
          ) : (
            <>
              <div className="modern-employee-list">
                {employees.map((employee) => {
                  const avatarUrl = getAvatarUrl(employee);

                  return (
                    <button
                      key={employee._id}
                      type="button"
                      className="modern-employee-list-item employee-list-button"
                      onClick={() => setSelectedEmployee(employee)}
                    >
                      <div className="modern-employee-list-avatar">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={`${employee.firstName} ${employee.lastName}`} />
                        ) : (
                          <div className="modern-employee-avatar-placeholder">
                            {employee.firstName?.charAt(0)}
                            {employee.lastName?.charAt(0)}
                          </div>
                        )}
                      </div>

                      <div className="modern-employee-list-content">
                        <div className="modern-employee-list-main">
                          <h5 className="modern-employee-name">
                            {employee.firstName} {employee.lastName}
                          </h5>
                          <p className="modern-employee-email">{employee.email}</p>
                          <p className="modern-employee-position">{employee.position}</p>
                          <p className="modern-employee-department">
                            {[employee.company, employee.department].filter(Boolean).join(' • ')}
                          </p>
                        </div>

                        <div className="modern-employee-list-info">
                          <div className="modern-employee-info-item">
                            <i className="bx bx-calendar"></i>
                            <span>{formatDate(employee.startDate)}</span>
                          </div>
                          <div className="modern-employee-info-item">
                            <i className="bx bx-map"></i>
                            <span>{employee.officeLocation || '-'}</span>
                          </div>
                          {employee.extension && (
                            <div className="modern-employee-info-item">
                              <i className="bx bx-phone-call"></i>
                              <span>Dahili {employee.extension}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="modern-employee-list-badges">
                        <span className={`modern-employee-badge ${getRoleBadgeClass(employee.role)}`}>
                          {getRoleText(employee.role)}
                        </span>
                        <span
                          className={`modern-employee-badge ${
                            employee.isActive
                              ? 'modern-employee-badge-active'
                              : 'modern-employee-badge-inactive'
                          }`}
                        >
                          {employee.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        <span className="modern-employee-badge modern-employee-badge-employee">
                          {employee.visibility?.directory === false ? 'Rehber kapalı' : 'Rehber açık'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="modern-hr-pagination">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="modern-hr-btn modern-hr-btn-secondary"
                  >
                    Önceki
                  </button>
                  <div className="modern-hr-pagination-info">
                    Sayfa {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="modern-hr-btn modern-hr-btn-secondary"
                  >
                    Sonraki
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EmployeeDetailModal
        employee={selectedEmployee}
        isOpen={Boolean(selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
      />
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default TumCalisanlarPage;
